import { useEffect, useState, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { forexApi, toBrokerAccount } from '@/services/forexApi';
import type { OnboardingOptions } from '@/services/forexApi';
import OnboardingProgress from './OnboardingProgress';
import type { BrokerAccount } from '@/lib/brokerAccounts';

type AccountType = 'mt4' | 'mt5';
type StepType = 'select' | 'form' | 'success';

interface MT45Form {
  server: string;
  login: string;
  password: string;
  brokerName: string;
}

const brokerPresets = [
  { name: 'IC Markets', server: 'ICMarketsSC-Live01', logo: '🏦' },
  { name: 'Pepperstone', server: 'Pepperstone-Live01', logo: '🌿' },
  { name: 'OANDA', server: 'api-fxtrade.oanda.com', logo: '⭕' },
  { name: 'XM Group', server: 'XMGlobal-Real01', logo: '✖' },
  { name: 'FP Markets', server: 'FPMarkets-Live01', logo: '🔵' },
  { name: 'Admiral Markets', server: 'AdmiralMarkets-Live', logo: '⚓' },
];

interface Props {
  onSuccess: () => void;
  onCancel: () => void;
}

export default function AddAccountFlow({ onSuccess, onCancel }: Props) {
  const { accessToken } = useAuth();
  const managed = import.meta.env.VITE_EA_MANAGED_ONBOARDING === 'true';
  const [options, setOptions] = useState<OnboardingOptions | null>(null);
  const [optionsError, setOptionsError] = useState('');
  const [consent, setConsent] = useState(false);
  const operationKey = useRef(crypto.randomUUID());
  const submittedPayload = useRef('');
  useEffect(() => {
    if (!managed) return;
    let cancelled = false;
    forexApi.onboardingOptions(accessToken).then((value) => { if (!cancelled) setOptions(value); })
      .catch(() => { if (!cancelled) setOptionsError('自动开通选项暂不可用，请稍后重试'); });
    return () => { cancelled = true; };
  }, [managed, accessToken]);
  const [accountType, setAccountType] = useState<AccountType>('mt4');
  const platformServers = options?.platform_servers ?? options?.servers.map((item) => ({ ...item, platform: 'mt4' as const })) ?? [];
  const mt5Available = managed && platformServers.some((item) => item.platform === 'mt5');
  const presets = managed ? platformServers.filter((item) => item.platform === accountType).map((item) => ({ name: item.broker, server: item.server, logo: '🏦' })) : brokerPresets;
  const [step, setStep] = useState<StepType>('select');
  const [mt45Form, setMt45Form] = useState<MT45Form>({ server: '', login: '', password: '', brokerName: '' });
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showPresets, setShowPresets] = useState(false);
  const [savedAccount, setSavedAccount] = useState<BrokerAccount | null>(null);
  const [submitError, setSubmitError] = useState('');

  const selectPlatform = (platform: AccountType) => {
    setAccountType(platform);
    setMt45Form({ server: '', login: '', password: '', brokerName: '' });
    setConsent(false);
    operationKey.current = crypto.randomUUID();
    submittedPayload.current = '';
    setStep('form');
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setSubmitError('');
    try {
      const account: BrokerAccount = {
        id: '',
        type: accountType,
        brokerName: mt45Form.brokerName || '自定义经纪商',
        accountNo: mt45Form.login,
        server: mt45Form.server,
        balance: '$0.00',
        margin: '$0.00',
        accountStatus: 'reviewing',
        connectedAt: new Date().toISOString().slice(0, 10),
        login: mt45Form.login,
        password: mt45Form.password,
      };

      const payloadIdentity = JSON.stringify([accountType, mt45Form.brokerName, mt45Form.server, mt45Form.login, mt45Form.password]);
      if (submittedPayload.current && submittedPayload.current !== payloadIdentity) operationKey.current = crypto.randomUUID();
      submittedPayload.current = payloadIdentity;
      const saved = await forexApi.submitFollowerAccount(account, accessToken, managed && options ? { consentVersion: options.consent_version, operationKey: operationKey.current } : undefined);
      setSavedAccount(toBrokerAccount(saved));
      setMt45Form((previous) => ({ ...previous, password: '' }));
      submittedPayload.current = '';
      setStep('success');
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : '提交失败，请重试');
    } finally {
      setSubmitting(false);
    }
  };

  const isFormValid = () => {
    return mt45Form.server.trim() && mt45Form.login.trim() && mt45Form.password.trim() && (!managed || (consent && platformServers.some((item) => item.platform === accountType && item.broker === mt45Form.brokerName && item.server === mt45Form.server)));
  };

  const accountDisplay = savedAccount
    ? savedAccount
    : {
      brokerName: mt45Form.brokerName || '自定义经纪商',
      accountNo: mt45Form.login ? '****' + mt45Form.login.slice(-4) : '****0000',
      server: mt45Form.server,
    };

  return (
    <div className="px-5">
      {/* Step: Select Type */}
      {step === 'select' && (
        <>
          <div className="mb-5">
            <p className="text-gray-900 font-semibold text-base mb-1">选择账户类型</p>
            <p className="text-gray-400 text-xs">绑定后 Agent 可连接您的交易账户自动执行</p>
          </div>

          <div className="bg-amber-50 border border-amber-100 rounded-2xl p-4 mb-5 flex gap-3">
            <div className="w-8 h-8 flex items-center justify-center rounded-xl bg-amber-100 flex-shrink-0">
              <i className="ri-shield-keyhole-line text-amber-500" />
            </div>
            <div>
              <p className="text-amber-700 font-semibold text-sm">安全说明</p>
              <p className="text-amber-600 text-xs mt-0.5 leading-relaxed">
                {managed ? '凭证由受保护的凭证服务托管，用于平台终端登录和故障恢复。提交后自动安装并验证 EA；开通不会自动创建跟随或启用策略。' : '此入口为人工开通。密码不会保存，审核后需在 Windows MT4 中手工登录账号。'}
              </p>
            </div>
          </div>

          <div className="flex flex-col gap-3">
            {/* MT4 — 可用 */}
            <button
              onClick={() => selectPlatform('mt4')}
              className="bg-white rounded-2xl p-4 flex items-start gap-4 text-left relative overflow-hidden"
              style={{ boxShadow: '0 2px 10px rgba(0,0,0,0.06)' }}
            >
              <span className="absolute top-3 right-3 text-xs px-2 py-0.5 rounded-full bg-green-500 text-white font-medium">
                可用
              </span>
              <div className="w-12 h-12 flex items-center justify-center rounded-2xl flex-shrink-0"
                style={{ background: '#6366f115' }}>
                <i className="ri-terminal-box-line text-xl" style={{ color: '#6366f1' }} />
              </div>
              <div className="flex-1">
                <p className="font-bold text-gray-900 text-sm mb-0.5">MetaTrader 4</p>
                <p className="text-gray-400 text-xs mb-2 leading-relaxed">使用 MT4 账号登录信息连接，选择平台已支持的经纪商服务器</p>
                <div className="flex gap-1.5 flex-wrap">
                  {['EA 交易', '主流经纪商', '稳定可靠'].map((tag) => (
                    <span key={tag} className="text-xs px-2 py-0.5 rounded-full bg-gray-50 text-gray-500">{tag}</span>
                  ))}
                </div>
              </div>
              <i className="ri-arrow-right-s-line text-gray-300 mt-1 flex-shrink-0" />
            </button>

            {/* Availability is supplied by the server catalog. */}
            <button type="button" disabled={!mt5Available} onClick={() => selectPlatform('mt5')}
              className={`rounded-2xl p-4 flex items-start gap-4 text-left relative overflow-hidden disabled:opacity-60 disabled:cursor-not-allowed ${mt5Available ? 'bg-white' : 'bg-gray-50 border border-gray-100'}`}
              style={{ boxShadow: mt5Available ? '0 2px 10px rgba(0,0,0,0.06)' : undefined }}>
              <span className={`absolute top-3 right-3 text-xs px-2 py-0.5 rounded-full text-white font-medium ${mt5Available ? 'bg-green-500' : 'bg-gray-300'}`}>
                {mt5Available ? '可用' : '暂不可用'}
              </span>
              <div className={`w-12 h-12 flex items-center justify-center rounded-2xl flex-shrink-0 ${mt5Available ? '' : 'bg-gray-100'}`}
                style={{ background: mt5Available ? '#6366f115' : undefined }}>
                <i className={`ri-terminal-window-line text-xl ${mt5Available ? '' : 'text-gray-400'}`} style={{ color: mt5Available ? '#6366f1' : undefined }} />
              </div>
              <div className="flex-1">
                <p className={`font-bold text-sm mb-0.5 ${mt5Available ? 'text-gray-900' : 'text-gray-400'}`}>MetaTrader 5</p>
                <p className={`text-xs mb-2 leading-relaxed ${mt5Available ? 'text-gray-400' : 'text-gray-300'}`}>使用 MT5 账号登录信息连接，选择平台已支持的经纪商服务器</p>
                <div className="flex gap-1.5 flex-wrap">
                  {['EA 交易', '模拟 / 实盘', 'Hedging 模式'].map((tag) => (
                    <span key={tag} className={`text-xs px-2 py-0.5 rounded-full ${mt5Available ? 'bg-gray-50 text-gray-500' : 'bg-gray-100 text-gray-300'}`}>{tag}</span>
                  ))}
                </div>
              </div>
              {mt5Available && <i className="ri-arrow-right-s-line text-gray-300 mt-1 flex-shrink-0" />}
            </button>

            {/* API — 置灰，后续开放 */}
            <div className="bg-gray-50 border border-gray-100 rounded-2xl p-4 flex items-start gap-4 text-left relative overflow-hidden opacity-60 cursor-not-allowed">
              <span className="absolute top-3 right-3 text-xs px-2 py-0.5 rounded-full bg-gray-300 text-white font-medium">
                后续开放
              </span>
              <div className="w-12 h-12 flex items-center justify-center rounded-2xl flex-shrink-0 bg-gray-100">
                <i className="ri-key-2-line text-xl text-gray-400" />
              </div>
              <div className="flex-1">
                <p className="font-bold text-gray-400 text-sm mb-0.5">经纪商 API 接入</p>
                <p className="text-gray-300 text-xs mb-2 leading-relaxed">通过经纪商 API Key 直连，权限可控，安全性更高</p>
                <div className="flex gap-1.5 flex-wrap">
                  {['OANDA', 'IC Markets', 'Alpaca'].map((tag) => (
                    <span key={tag} className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-300">{tag}</span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {managed && optionsError && <p role="alert" className="text-red-500 text-sm py-3">{optionsError}</p>}
      {/* Step: Form */}
      {step === 'form' && (
        <>
          <div className="flex flex-col gap-4">
            {/* Broker picker */}
            <div>
              <label className="text-gray-700 text-sm font-medium block mb-2">经纪商（快速选择）</label>
              <button
                onClick={() => setShowPresets(!showPresets)}
                className="w-full bg-white rounded-xl px-4 py-3 flex items-center justify-between border border-gray-100 text-sm"
                style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}
              >
                <span className={mt45Form.brokerName ? 'text-gray-900' : 'text-gray-400'}>
                  {mt45Form.brokerName || (managed ? '选择经纪商及服务器' : '选择常用经纪商（可选）')}
                </span>
                <i className={`text-gray-400 ${showPresets ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}`} />
              </button>
              {showPresets && (
                <div className="bg-white rounded-xl mt-1 border border-gray-100 overflow-hidden" style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
                  {presets.map((b, idx) => (
                    <button key={`${b.name}:${b.server}`} onClick={() => {
                      setMt45Form({ ...mt45Form, brokerName: b.name, server: b.server });
                      setShowPresets(false);
                    }}
                      className={`w-full px-4 py-2.5 flex items-center gap-3 text-sm active:bg-gray-50 ${idx > 0 ? 'border-t border-gray-50' : ''}`}
                    >
                      <span className="text-base">{b.logo}</span>
                      <div className="text-left">
                        <p className="text-gray-900 text-sm">{b.name}</p>
                        <p className="text-gray-400 text-xs">{b.server}</p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div>
              <label className="text-gray-700 text-sm font-medium block mb-2">
                服务器地址 <span className="text-red-400">*</span>
              </label>
              <input
                readOnly={managed}
                value={mt45Form.server}
                onChange={(e) => setMt45Form({ ...mt45Form, server: e.target.value })}
                placeholder={`选择经纪商对应的 ${accountType.toUpperCase()} 服务器`}
                className="w-full bg-white rounded-xl px-4 py-3 text-sm text-gray-900 border border-gray-100 outline-none"
                style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}
              />
              <p className="text-gray-400 text-xs mt-1 pl-1">可在 {accountType.toUpperCase()} 客户端「工具 &gt; 选项 &gt; 服务器」查看</p>
            </div>

            <div>
              <label className="text-gray-700 text-sm font-medium block mb-2">
                账号 <span className="text-red-400">*</span>
              </label>
              <input
                value={mt45Form.login}
                onChange={(e) => setMt45Form({ ...mt45Form, login: e.target.value })}
                placeholder={`${accountType.toUpperCase()} 账号数字`}
                className="w-full bg-white rounded-xl px-4 py-3 text-sm text-gray-900 border border-gray-100 outline-none"
                style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}
              />
            </div>

            <div>
              <label className="text-gray-700 text-sm font-medium block mb-2">
                密码 <span className="text-red-400">*</span>
              </label>
              <div className="relative">
                <input
                  value={mt45Form.password}
                  onChange={(e) => setMt45Form({ ...mt45Form, password: e.target.value })}
                  placeholder={`${accountType.toUpperCase()} 账户登录密码`}
                  type={showPassword ? 'text' : 'password'}
                  className="w-full bg-white rounded-xl px-4 py-3 pr-11 text-sm text-gray-900 border border-gray-100 outline-none"
                  style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}
                />
                <button onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-7 h-7 flex items-center justify-center text-gray-400">
                  <i className={showPassword ? 'ri-eye-off-line' : 'ri-eye-line'} />
                </button>
              </div>
              <p className="text-gray-400 text-xs mt-1 pl-1">使用 {accountType.toUpperCase()} 交易密码；投资者只读密码无法执行交易</p>
            </div>
          </div>

          {managed && <label className="flex gap-2 text-xs text-gray-600 my-4"><input type="checkbox" checked={consent} onChange={(event) => setConsent(event.target.checked)} />授权平台托管凭证，在 Windows 终端运行 EA 并恢复连接。跟随与策略启用需另行设置。</label>}
          <button
            onClick={handleSubmit}
            disabled={submitting || !isFormValid()}
            className="w-full mt-6 font-semibold text-sm flex-shrink-0"
            style={{
              height: '48px',
              minHeight: '48px',
              borderRadius: '16px',
              background: submitting || !isFormValid() ? '#E5E7EB' : 'linear-gradient(135deg, #6366f1, #7c3aed)',
              color: submitting || !isFormValid() ? '#9CA3AF' : '#FFFFFF',
              border: 'none',
              padding: 0,
              WebkitAppearance: 'none',
              appearance: 'none',
              cursor: submitting || !isFormValid() ? 'not-allowed' : 'pointer',
              display: 'block',
              position: 'relative',
            }}
          >
            <span className="absolute inset-0 flex items-center justify-center gap-2">
              {submitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                  提交中...
                </>
              ) : (
                <>
                  <i className="ri-send-plane-line text-lg" />
                  {managed ? '提交并自动接入' : '提交审核'}
                </>
              )}
            </span>
          </button>
          {submitError && (
            <p className="mt-3 text-xs text-red-500 text-center">{submitError}</p>
          )}
        </>
      )}

      {/* Step: Success — 接入中 */}
      {step === 'success' && savedAccount && (
        <div className="flex flex-col items-center py-10">
          {/* 接入中图标 */}
          <div className="w-20 h-20 rounded-full bg-amber-50 flex items-center justify-center mb-5">
            <i className="ri-time-line text-amber-500 text-4xl" />
          </div>
          <p className="text-gray-900 font-bold text-xl mb-2">账号已提交</p>
          <p className="text-gray-400 text-sm text-center mb-1 leading-relaxed px-4">
            {managed ? `平台正在自动验证并接入你的 ${accountType.toUpperCase()} 交易账号。` : `平台正在审核你的 ${accountType.toUpperCase()} 交易账号。`}
          </p>
          <p className="text-gray-400 text-sm text-center mb-6 leading-relaxed px-4">
            {managed ? '提交不代表已连接，请以接入状态为准。平台会自动准备终端、登录账号并验证 EA；完成后仍需另行设置跟随与策略授权。' : '提交不代表已连接。管理员创建人工开通任务后，需要在 Windows MT4 手工登录并完成 EA 心跳验证。'}
          </p>

          {managed && <OnboardingProgress accountId={savedAccount.id} />}
          {/* 提交信息卡片 */}
          <div className="w-full bg-white rounded-2xl p-4 mb-6" style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-amber-100 flex-shrink-0">
                <i className="ri-loader-4-line text-amber-500 text-lg animate-spin" />
              </div>
              <div>
                <p className="text-gray-900 font-semibold text-sm">{managed ? '自动接入中' : '审核中'}</p>
                <p className="text-gray-400 text-xs">{managed ? '平台正在验证账号并准备跟单环境' : '平台正在审核并准备跟单环境'}</p>
              </div>
            </div>
            <div className="flex flex-col gap-0 border-t border-gray-50 pt-3">
              <div className="flex justify-between items-center py-2">
                <span className="text-gray-400 text-sm">经纪商</span>
                <span className="text-gray-900 font-semibold text-sm">{accountDisplay.brokerName}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-t border-gray-50">
                <span className="text-gray-400 text-sm">账号</span>
                <span className="text-gray-900 font-semibold text-sm">{accountDisplay.accountNo}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-t border-gray-50">
                <span className="text-gray-400 text-sm">服务器</span>
                <span className="text-gray-900 font-semibold text-sm">{accountDisplay.server}</span>
              </div>
            </div>
          </div>

          <div className="flex gap-3 w-full">
            <button
              onClick={onSuccess}
              className="flex-1 text-white font-semibold py-3 rounded-2xl text-sm flex-shrink-0"
              style={{
                background: 'linear-gradient(135deg, #6366f1, #7c3aed)',
                border: 'none',
                padding: 0,
                WebkitAppearance: 'none',
                appearance: 'none',
                height: '48px',
                minHeight: '48px',
                display: 'block',
                position: 'relative',
              }}
            >
              <span className="absolute inset-0 flex items-center justify-center">返回交易账号列表</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
